import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import Lozenge from '@atlaskit/lozenge';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import { htmlToADF } from '@atlassian/html-adf-transformer';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useSpaceIdentifiers } from '@confluence/space-utils/entry-points/useSpaceIdentifiers.ts';

import {
	ANALYTICS_ORIGIN,
	ANALYTICS_PAGE_MODE,
	ANALYTICS_UI_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import { CreateSnippetModal } from '../modals/CreateSnippetModal.tsx';

import { getHTMLString } from './getHTMLWithinContent.ts';

const messages = defineMessages({
	createBlockTemplate: {
		id: 'snippets.highlightActions.createBlockTemplate',
		defaultMessage: 'Create block template',
		description: 'Label for the create block template button in the highlight actions toolbar',
	},
	newLozenge: {
		id: 'snippets.highlightActions.newLozenge',
		defaultMessage: 'New',
		description: 'Lozenge label indicating this is a new feature',
	},
});

/**
 * Shared click handler logic for snippet creation from the highlight actions toolbar.
 * Extracts ADF from the selection and opens the CreateSnippetModal.
 */
const useSnippetClickHandler = (contentId: string) => {
	const { showModal } = useDialogs();
	const analyticsOrigin = ANALYTICS_ORIGIN.CONTEXTUAL_TOOLBAR;
	const { fireButtonClickedAnalytic } = useSnippetAnalytics(analyticsOrigin);
	const { spaceId, spaceKey } = useSpaceIdentifiers();

	return () => {
		const html = getHTMLString(window.getSelection());
		const docAdf = htmlToADF(html);

		// Strip the doc wrapper to get the inner content nodes
		const adfContent = docAdf?.content?.filter((node: unknown) => node !== undefined);
		if (!adfContent || adfContent.length === 0) {
			return;
		}

		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET, {
			pageMode: ANALYTICS_PAGE_MODE.VIEW,
			selectionLength: JSON.stringify(adfContent)?.length,
		});

		showModal(CreateSnippetModal, {
			adf: docAdf,
			contentId,
			spaceId: spaceId ?? '',
			spaceKey: spaceKey ?? '',
			// editorAPI is null because we don't need it in VIEW mode
			// and this button is only present in VIEW mode.
			editorAPI: null,
			analyticsOrigin,
		});
	};
};

type HighlightActionsSnippetButtonProps = {
	contentId: string;
};

/**
 * Icon-only button variant for use in the main highlight actions toolbar (legacy toolbar).
 */
export const HighlightActionsSnippetButton = ({
	contentId,
}: HighlightActionsSnippetButtonProps) => {
	const intl = useIntl();
	const handleClick = useSnippetClickHandler(contentId);

	return (
		<IconButton
			icon={TemplateIcon}
			appearance="subtle"
			label={intl.formatMessage(messages.createBlockTemplate)}
			isTooltipDisabled={false}
			onClick={handleClick}
		/>
	);
};

/**
 * Dropdown menu item variant for use in the "..." overflow menu (new toolbar).
 * Shows "Create block template" text with TemplateIcon and a "New" lozenge.
 */
export const HighlightActionsSnippetDropdownItem = ({
	contentId,
}: HighlightActionsSnippetButtonProps) => {
	const intl = useIntl();
	const handleClick = useSnippetClickHandler(contentId);

	return (
		<DropdownItem
			elemBefore={<TemplateIcon label="" />}
			elemAfter={<Lozenge appearance="new">{intl.formatMessage(messages.newLozenge)}</Lozenge>}
			onClick={handleClick}
		>
			{intl.formatMessage(messages.createBlockTemplate)}
		</DropdownItem>
	);
};
